import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { EligibilityHero } from './components/EligibilityHero';
import { EligibilityChecklist } from './components/EligibilityChecklist';
import { CityRules } from './components/CityRules';
import { ResultsCTA } from './components/ResultsCTA';

interface ChecklistItem {
  label: string;
  status: 'pass' | 'warning' | 'fail';
  detail: string;
}

// City‑specific principal residence requirements
const cityRequiresPR: Record<string, boolean> = {
  Vancouver: true,
  'North Vancouver': true,
  Burnaby: true,
  Surrey: true,
  Langley: true,
  Victoria: true,
  Toronto: true,
  Whistler: false,
  Calgary: false,
  Edmonton: false,
};

// City‑specific night caps for entire‑home
const cityNightCaps: Record<string, number> = {
  Vancouver: 365, // No night cap - can host 365 days if principal residence
  'North Vancouver': 365,
  Burnaby: 90,
  Surrey: 365,
  Langley: 365,
  Victoria: 365,
  Toronto: 180,
  Whistler: 365,
  Calgary: 365,
  Edmonton: 365,
};

function evaluateEligibility(
  data: Record<string, string>
): {
  score: number;
  status: 'eligible' | 'conditional' | 'not-eligible';
  items: ChecklistItem[];
} {
  const items: ChecklistItem[] = [];
  let score = 0;
  const city = data.city || 'Other';
  const requiresPR = cityRequiresPR[city] ?? true;

  // 1. Principal Residence
  if (requiresPR) {
    if (data.principalResidence === 'Yes') {
      items.push({
        label: 'Principal Residence Requirement',
        status: 'pass',
        detail: `${city} requires you to live in the property. You confirmed this is your principal residence.`,
      });
      score += 20;
    } else if (data.principalResidence === 'Part time') {
      items.push({
        label: 'Principal Residence Requirement',
        status: 'warning',
        detail: `${city} requires the property to be your principal residence. Part-time occupancy may not qualify — you’ll need to verify with the city.`,
      });
      score += 10;
    } else {
      items.push({
        label: 'Principal Residence Requirement',
        status: 'fail',
        detail: `${city} requires you to live in the property as your principal residence to host short‑term rentals. This is a key barrier.`,
      });
    }
  } else {
    items.push({
      label: 'Principal Residence Requirement',
      status: 'pass',
      detail: `${city} does not require principal residence for STR operators. Investors are welcome.`,
    });
    score += 20;
  }

  // 2. Property Type
  const riskyTypes = ['Secondary suite', 'Laneway house'];
  if (data.propertyType && !riskyTypes.includes(data.propertyType)) {
    items.push({
      label: 'Property Type',
      status: 'pass',
      detail: `Your property type (${data.propertyType}) is generally eligible for short‑term rental licensing in most cities.`,
    });
    score += 15;
  } else if (riskyTypes.includes(data.propertyType!)) {
    items.push({
      label: 'Property Type',
      status: 'warning',
      detail: `${data.propertyType} properties may have additional restrictions in ${city}. Some cities limit STRs to the primary dwelling unit only.`,
    });
    score += 8;
  } else {
    items.push({
      label: 'Property Type',
      status: 'warning',
      detail: 'Property type was not specified. Different property types have different eligibility rules.',
    });
    score += 5;
  }

  // 3. Strata Restrictions
  if (data.strataRestrictions === 'No' || data.strataRestrictions === 'Not applicable') {
    items.push({
      label: 'Strata / Condo Restrictions',
      status: 'pass',
      detail: 'No strata or condo restrictions blocking your ability to host. This is a green light.',
    });
    score += 20;
  } else if (data.strataRestrictions === 'Yes') {
    items.push({
      label: 'Strata / Condo Restrictions',
      status: 'fail',
      detail: 'Your building or strata restricts short‑term rentals. This is a significant barrier that may prevent you from legally hosting.',
    });
  } else {
    items.push({
      label: 'Strata / Condo Restrictions',
      status: 'warning',
      detail: 'You’re unsure about strata restrictions. We strongly recommend checking your strata bylaws and meeting minutes before proceeding.',
    });
    score += 8;
  }

  // 4. Currently Rented Long‑Term
  if (data.currentlyRented === 'No' || data.currentlyRented === 'Vacant') {
    items.push({
      label: 'Current Tenancy Status',
      status: 'pass',
      detail: 'The property is not currently rented long‑term, so there are no tenant displacement concerns.',
    });
    score += 15;
  } else if (data.currentlyRented === 'Yes') {
    items.push({
      label: 'Current Tenancy Status',
      status: 'fail',
      detail: 'The property is currently rented long‑term. You cannot evict a tenant to convert to short‑term rental. You’ll need to wait until the tenancy ends naturally.',
    });
  } else {
    items.push({
      label: 'Current Tenancy Status',
      status: 'warning',
      detail: 'Tenancy status was not specified. If the property has a long‑term tenant, you cannot convert it to STR.',
    });
    score += 5;
  }

  // 5. Licensing / Registration
  if (data.appliedForLicense === 'Yes') {
    items.push({
      label: 'Business Licence / Registration',
      status: 'pass',
      detail: 'You’ve already applied for the required licence or registration. Make sure it’s approved before listing.',
    });
    score += 15;
  } else if (data.appliedForLicense === 'No') {
    items.push({
      label: 'Business Licence / Registration',
      status: 'warning',
      detail: `You haven’t applied for a licence yet. ${city} requires a business licence and/or STR registration before you can legally host.`,
    });
    score += 5;
  } else {
    items.push({
      label: 'Business Licence / Registration',
      status: 'warning',
      detail: 'You’re unsure about licensing. Most cities require a business licence and provincial registration. We can help you navigate this.',
    });
    score += 5;
  }

  // 6. Willingness to Comply
  if (data.willingToComply === 'Yes') {
    items.push({
      label: 'Regulatory Compliance Willingness',
      status: 'pass',
      detail: 'You’re willing to operate within regulations. This is essential for long‑term, sustainable hosting.',
    });
    score += 15;
  } else if (data.willingToComply === 'No') {
    items.push({
      label: 'Regulatory Compliance Willingness',
      status: 'fail',
      detail: 'Operating outside regulations carries serious risks including fines, licence revocation, and legal action. We strongly recommend compliance.',
    });
  } else {
    items.push({
      label: 'Regulatory Compliance Willingness',
      status: 'warning',
      detail: 'You’re unsure about compliance. We can help you understand exactly what’s required — it’s often simpler than you think.',
    });
    score += 8;
  }

  // Determine overall status
  const failCount = items.filter((i) => i.status === 'fail').length;
  const warnCount = items.filter((i) => i.status === 'warning').length;

  let status: 'eligible' | 'conditional' | 'not-eligible';
  if (failCount >= 2) {
    status = 'not-eligible';
  } else if (failCount === 1 || warnCount >= 3) {
    status = 'conditional';
  } else {
    status = 'eligible';
  }

  // Cap score at 100
  score = Math.min(score, 100);

  return { score, status, items };
}

export default function EligibilityResultsPage() {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<{
    score: number;
    status: 'eligible' | 'conditional' | 'not-eligible';
    items: ChecklistItem[];
  } | null>(null);
  const [city, setCity] = useState<string>('');

  useEffect(() => {
    try {
      const data: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        data[key] = value;
      });

      if (data.city) {
        setCity(data.city);
      }

      const evaluation = evaluateEligibility(data);
      setResult(evaluation);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Eligibility evaluation failed:', err);
      // Graceful fallback UI could be added here
    }
  }, [searchParams]);

  if (!result) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-[#882629] animate-spin"></i>
          <p className="mt-4 text-[#505A5A]">Analyzing your property...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link
            to="/property-assessment"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Retake Assessment
          </Link>
          <Link to="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/55893c4a5915be14917a0a81b6ec721d/f5868736a4ed9c94ff4146ae0bef5b91.png"
              alt="Furnished Rental Management Canada Logo"
              className="h-12 w-auto"
            />
          </Link>
        </div>
      </header>

      <EligibilityHero score={result.score} status={result.status} city={city || 'your city'} />
      <EligibilityChecklist items={result.items} />
      {city && city !== 'Other' && <CityRules city={city} />}
      <ResultsCTA status={result.status} />

      {/* Footer */}
      <footer className="bg-[#505A5A] text-white py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-300">
            &copy; 2025 Compliant Rental Management. All rights reserved.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-sm text-gray-300 hover:text-white transition-colors"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
